import { FC, useMemo } from "react";
import capitalize from "lodash/capitalize";
import { faBullseyeArrow } from "@fortawesome/pro-duotone-svg-icons";
import { faTelescope } from "@fortawesome/pro-duotone-svg-icons";
import { faMessageCode, faSliders } from "@fortawesome/pro-regular-svg-icons";
import { faBan } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Group, Text, Tooltip, useMantineTheme } from "@mantine/core";

import { Confidence, Severity } from "@protos/common/v1/common";
import { Issue, IssueType } from "@protos/issues/v1/issues";
import { SeverityIcon } from "@shared/components";
import { useSupportsFeature } from "@shared/hooks";
import { Rule } from "@shared/types";
import { getDisplaySeverity, isSecureDefault } from "@shared/utils";

import { EpssLabel } from "../../EpssLabel";
import { SecureDefaultInfo } from "../../SecureDefaultInfo";

const CONFIDENCE_TO_TOOLTIP_LABEL: Record<Confidence, string> = {
  [Confidence.HIGH]:
    "High confidence rules detect true positives most of the time.",
  [Confidence.MEDIUM]:
    "Medium confidence rules might detect some false positives.",
  [Confidence.LOW]:
    "Low confidence rules are expected to detect many false positives.",
  [Confidence.UNSPECIFIED]:
    "Low confidence rules are expected to detect many false positives.",
};

const POLICY_TO_TOOLTIP_LABEL: { [key: string]: string } = {
  monitor: "Monitoring rules only collect findings in Semgrep AppSec Platform.",
  comment: "Commenting rules leave a dismissible comment for developers.",
  block: "Blocking rules leave a merge-blocking comment for developers.",
};

export interface RuleIconInfoListProps {
  finding: Issue;
  rule?: Rule;
}

/**
 *
 * @param finding - a formatted finding
 * @param rule - the rule that the finding was generated from
 * Note: the rule information on the finding can be out-of-date, which is why we need to retrieve information from the rule itself.
 * @returns a list of icon and label pairs in a row. Specifically for use with RuleInformationCard component.
 */
export const RuleIconInfoList: FC<RuleIconInfoListProps> = ({
  finding,
  rule,
}) => {
  const { colors } = useMantineTheme();
  const textColor = colors["gray"][6];

  const hasEpss = useSupportsFeature("hasEpss");

  // Severity values take one form in Rule objects and another form in findings. This logic helps compare the two.
  // TODO: make everything use the common Severity protobuf type.
  const severityProto = rule ? getDisplaySeverity(rule, finding) : Severity.LOW;

  let severityLabel: string = "Low";
  switch (severityProto) {
    case Severity.MEDIUM:
      severityLabel = "Medium";
      break;
    case Severity.HIGH:
      severityLabel = "High";
      break;
    case Severity.CRITICAL:
      severityLabel = "Critical";
      break;
    case Severity.UNSPECIFIED:
    case Severity.LOW:
      severityLabel = "Low";
  }

  let confidenceLabel: string = "LOW";
  switch (finding.confidence) {
    case Confidence.HIGH:
      confidenceLabel = "High";
      break;
    case Confidence.MEDIUM:
      confidenceLabel = "Medium";
      break;
    case Confidence.LOW:
    case Confidence.UNSPECIFIED:
      confidenceLabel = "Low";
      break;
  }

  const policy = useMemo(() => {
    switch (finding.policySlug) {
      case "rule-board-block":
        return { name: "block", color: "red.8", icon: faBan };
      case "rule-board-pr-comments":
        return { name: "comment", color: "orange.6", icon: faMessageCode };
      case "rule-board-audit":
        return { name: "monitor", color: "blue.6", icon: faTelescope };
      default:
        return {
          name: finding.policySlug ?? "",
          color: "teal",
          icon: faSliders,
        };
    }
  }, [finding.policySlug]);

  return (
    <Group gap={20} fw={500}>
      {isSecureDefault(rule) && (
        <SecureDefaultInfo c={textColor} iconSize="lg" />
      )}
      {hasEpss &&
        finding.issueType === IssueType.SCA &&
        finding.scaAttributes?.epssScore && (
          <EpssLabel epss={finding.scaAttributes.epssScore} />
        )}
      <Tooltip
        multiline
        label={
          severityProto && (
            <>
              <Text>Generated with previous version of the rule.</Text>
              <Text>
                Newest version of the rule has been updated to
                {` ${severityLabel} `}
                severity.
              </Text>
            </>
          )
        }
        withArrow
        disabled={
          finding.issueType === IssueType.SCA ||
          finding.issueType === IssueType.SECRETS ||
          severityProto === finding.severity
        }
      >
        <Group gap="xs">
          <SeverityIcon severity={severityProto} />
          <Text c={textColor}>{severityLabel} severity</Text>
        </Group>
      </Tooltip>
      {finding.issueType === IssueType.SAST &&
        finding.confidence &&
        finding.confidence !== Confidence.UNSPECIFIED && (
          <Tooltip
            label={CONFIDENCE_TO_TOOLTIP_LABEL[finding.confidence]}
            w={220}
            multiline
            withinPortal
            withArrow
          >
            <Group gap="xs">
              <Text>
                <FontAwesomeIcon icon={faBullseyeArrow} size="lg" fixedWidth />
              </Text>
              <Text c={textColor}>{confidenceLabel} confidence</Text>
            </Group>
          </Tooltip>
        )}
      {finding.issueType === IssueType.SAST && (
        <Tooltip
          label={POLICY_TO_TOOLTIP_LABEL[policy.name]}
          disabled={["monitor", "comment", "block"].indexOf(policy.name) === -1}
          w={220}
          multiline
          withinPortal
          withArrow
        >
          <Group gap="xs">
            <Text c={policy.color}>
              <FontAwesomeIcon icon={policy.icon} size="lg" fixedWidth />
            </Text>
            <Text c={textColor}>{capitalize(policy.name)}</Text>
          </Group>
        </Tooltip>
      )}
    </Group>
  );
};
